/** Pure formatting for billing: signed credit amounts, prices, and readable ledger descriptions */

import type { TranslationKey } from '../../i18n'

/** "+1,000" for credits added, "−2" (true minus sign) for credits spent, "0" otherwise */
export function formatSignedCredits(amount: number, locale: string): string {
  const abs = new Intl.NumberFormat(locale).format(Math.abs(amount))
  if (amount > 0) return `+${abs}`
  if (amount < 0) return `−${abs}`
  return abs
}

/** Currencies the providers report in whole units (no minor unit) */
const ZERO_DECIMAL_CURRENCIES = new Set(['vnd', 'jpy', 'krw'])

/**
 * Price with currency symbol. `amount` is in minor units (cents) except for zero-decimal
 * currencies such as VND; whole amounts drop the trailing ".00".
 */
export function formatPrice(amount: number, currency: string, locale: string): string {
  const code = currency.trim().toLowerCase() || 'usd'
  const value = ZERO_DECIMAL_CURRENCIES.has(code) ? amount : amount / 100
  const fractionDigits = Number.isInteger(value) ? 0 : 2
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code.toUpperCase(),
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)
  } catch {
    // Unknown ISO code from the provider: still show the amount
    return `${new Intl.NumberFormat(locale).format(value)} ${code.toUpperCase()}`
  }
}

const TYPE_KEYS: Record<string, TranslationKey> = {
  deduct: 'billing.history.type.deduct',
  purchase: 'billing.history.type.purchase',
  free_grant: 'billing.history.type.free_grant',
  refund: 'billing.history.type.refund',
  expire: 'billing.history.type.expire',
}

/** Translation key for a ledger type, or null for a type this UI does not know */
export function transactionTypeKey(type: string): TranslationKey | null {
  return TYPE_KEYS[type] ?? null
}

/** Billable actions as charged by the API credit guard (method + path prefix) */
const ACTIONS: { method: string; prefix: string; suffix?: string; key: TranslationKey }[] = [
  // Version snapshots (POST /api/documents/:id/versions) are charged too; listed first so they are not read as a create
  { method: 'POST', prefix: '/api/documents', suffix: '/versions', key: 'billing.history.action.docSnapshot' },
  { method: 'POST', prefix: '/api/documents', key: 'billing.history.action.docCreate' },
  { method: 'PUT', prefix: '/api/documents', key: 'billing.history.action.docEdit' },
  { method: 'PATCH', prefix: '/api/documents', key: 'billing.history.action.docEdit' },
  { method: 'GET', prefix: '/api/search', key: 'billing.history.action.search' },
  { method: 'POST', prefix: '/api/ai', key: 'billing.history.action.ai' },
  { method: 'POST', prefix: '/api/uploads', key: 'billing.history.action.upload' },
  { method: 'POST', prefix: '/api/import', key: 'billing.history.action.import' },
  { method: 'POST', prefix: '/api/sites', key: 'billing.history.action.site' },
]

export type TransactionDescription =
  /** A known action, shown translated; `refund` marks a reversed charge */
  | { kind: 'action'; key: TranslationKey; refund: boolean; raw: string }
  /** Free text from the API (e.g. "Purchased Starter"), shown as-is */
  | { kind: 'text'; text: string }
  | { kind: 'none' }

/**
 * The credit guard stores descriptions as "METHOD /api/path" (refunds prefixed "Refund: ").
 * Map those to a readable action; anything else is shown verbatim.
 */
export function describeTransaction(description: string | null | undefined): TransactionDescription {
  const text = description?.trim() ?? ''
  if (!text) return { kind: 'none' }
  const refund = text.startsWith('Refund: ')
  const body = refund ? text.slice('Refund: '.length) : text
  const match = /^([A-Z]+) (\/\S*)$/.exec(body)
  if (match) {
    const [, method, path] = match
    const action = ACTIONS.find(
      (a) => a.method === method && (path === a.prefix || path.startsWith(`${a.prefix}/`)) && (!a.suffix || path.endsWith(a.suffix)),
    )
    if (action) return { kind: 'action', key: action.key, refund, raw: body }
  }
  return { kind: 'text', text }
}

export type InfiniteList<T> = {
  pages: { data: T[]; total: number }[]
}

/** All loaded rows of an offset-paged list, once each */
export function flattenPages<T extends { id: string }>(data: InfiniteList<T> | undefined): { items: T[]; total: number } {
  const pages = data?.pages ?? []
  // Offset pages shift when new charges land while reading (each search is charged); keep the first copy of each row
  const seen = new Set<string>()
  const items = pages.flatMap((p) => p.data).filter((item) => {
    if (seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
  return { items, total: pages[pages.length - 1]?.total ?? 0 }
}

/** Parse a date input value (YYYY-MM-DD) as local midnight, or null */
function localDay(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return null
  const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(day.getTime()) ? null : day
}

/** Inclusive ms range for two date inputs in local time (start of `from`, end of `to`); empty inputs stay open */
export function dateRangeToMs(from: string, to: string): { from?: number; to?: number } {
  const start = localDay(from)
  const end = localDay(to)
  return {
    from: start ? start.getTime() : undefined,
    to: end ? new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1).getTime() - 1 : undefined,
  }
}

export type TransactionActor =
  | { kind: 'apiKey'; name: string | null }
  | { kind: 'user'; name: string; email: string | null }
  | { kind: 'none' }

/** Who a ledger row is attributed to: the API key when one made the request, else the workspace member */
export function transactionActor(tx: {
  apiKeyId?: string | null
  apiKeyName?: string | null
  userName?: string | null
  userEmail?: string | null
}): TransactionActor {
  if (tx.apiKeyId) return { kind: 'apiKey', name: tx.apiKeyName ?? null }
  const name = tx.userName || tx.userEmail
  return name ? { kind: 'user', name, email: tx.userEmail ?? null } : { kind: 'none' }
}

/** Countdown as HH:MM:SS (e.g. "23:59:05"); "00:00:00" once the time has passed */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
